import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, FolderPlus, Package, Check } from 'lucide-react';
import { toast } from 'sonner';
import apiClient, { getAuthToken } from '@/utils/api';

const MenuPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: '', id: null });
  const [newCategory, setNewCategory] = useState({ name: '', order: 0 });
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    available: true
  });

  // Inventory & Recipe state (v1.0.5)
  const [isInventoryEnabled, setIsInventoryEnabled] = useState(localStorage.getItem('inventory_enabled') === 'true');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [showRecipeDialog, setShowRecipeDialog] = useState(false);
  const [selectedRecipeItem, setSelectedRecipeItem] = useState(null);
  const [recipeList, setRecipeList] = useState([]);
  const [selectedInvId, setSelectedInvId] = useState('');
  const [recipeQty, setRecipeQty] = useState('');

  useEffect(() => {
    if (!getAuthToken()) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [catRes, itemsRes] = await Promise.all([
        apiClient.get('/menu/categories'),
        apiClient.get('/menu/items')
      ]);
      setCategories(catRes.data);
      setItems(itemsRes.data);

      if (localStorage.getItem('inventory_enabled') === 'true') {
        try {
          const invRes = await apiClient.get('/inventory');
          setInventoryItems(invRes.data);
          if (invRes.data.length > 0) setSelectedInvId(invRes.data[0].id);
        } catch (e) {}
      }
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    }
  };

  const handleOpenRecipeDialog = (item) => {
    setSelectedRecipeItem(item);
    setRecipeList(item.recipe || []);
    if (inventoryItems.length > 0) setSelectedInvId(inventoryItems[0].id);
    setRecipeQty('');
    setShowRecipeDialog(true);
  };

  const handleAddRecipeItem = () => {
    if (!selectedInvId || !recipeQty || parseFloat(recipeQty) <= 0) {
      toast.error('Lütfen hammadde seçin ve geçerli bir miktar girin.');
      return;
    }
    const invItem = inventoryItems.find(i => i.id === selectedInvId);
    if (!invItem) return;

    const newItem = {
      inventory_item_id: invItem.id,
      inventory_item_name: invItem.name,
      quantity: parseFloat(recipeQty),
      unit: invItem.unit
    };

    setRecipeList([...recipeList, newItem]);
    setRecipeQty('');
  };

  const handleRemoveRecipeItem = (idx) => {
    const newList = [...recipeList];
    newList.splice(idx, 1);
    setRecipeList(newList);
  };

  const handleSaveRecipe = async () => {
    try {
      await apiClient.put(`/menu/items/${selectedRecipeItem.id}`, {
        ...selectedRecipeItem,
        recipe: recipeList
      });
      toast.success('Hammadde reçetesi başarıyla güncellendi.');
      setShowRecipeDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Reçete kaydedilirken hata oluştu.');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error('Kategori adı gerekli');
      return;
    }

    try {
      await apiClient.post('/menu/categories', newCategory);
      toast.success('Kategori eklendi');
      setNewCategory({ name: '', order: 0 });
      setShowCategoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Kategori eklenirken hata oluştu');
    }
  };

  const handleDeleteCategory = (categoryId) => {
    setDeleteConfirm({ isOpen: true, type: 'category', id: categoryId });
  };

  const handleAddOrUpdateItem = async () => {
    if (!newItem.name || !newItem.price || !newItem.category_id) {
      toast.error('Ad, fiyat ve kategori gerekli');
      return;
    }

    if (parseFloat(newItem.price) < 0) {
      toast.error('Fiyat 0\'dan küçük olamaz');
      return;
    }

    try {
      const itemData = {
        ...newItem,
        price: parseFloat(newItem.price)
      };

      if (editingItem) {
        await apiClient.put(`/menu/items/${editingItem.id}`, itemData);
        toast.success('Menü öğesi güncellendi');
      } else {
        await apiClient.post('/menu/items', itemData);
        toast.success('Menü öğesi eklendi');
      }

      setNewItem({ name: '', description: '', price: '', category_id: '', image_url: '', available: true });
      setEditingItem(null);
      setShowItemDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Menü öğesi kaydedilirken hata oluştu');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setNewItem({
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      category_id: item.category_id,
      image_url: item.image_url || '',
      available: item.available
    });
    setShowItemDialog(true);
  };

  const handleDeleteItem = (itemId) => {
    setDeleteConfirm({ isOpen: true, type: 'item', id: itemId });
  };

  const confirmDelete = async () => {
    const { type, id } = deleteConfirm;
    setDeleteConfirm({ isOpen: false, type: '', id: null });
    
    if (type === 'category') {
      try {
        await apiClient.delete(`/menu/categories/${id}`);
        toast.success('Kategori silindi');
        fetchData();
      } catch (error) {
        toast.error('Kategori silinirken hata oluştu');
      }
    } else if (type === 'item') {
      try {
        await apiClient.delete(`/menu/items/${id}`);
        toast.success('Menü öğesi silindi');
        fetchData();
      } catch (error) {
        toast.error('Menü öğesi silinirken hata oluştu');
      }
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : 'Bilinmeyen';
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 600;
        const MAX_HEIGHT = 600;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setNewItem({ ...newItem, image_url: dataUrl });
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Menü Yönetimi
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Kategoriler ve menü öğelerini yönetin</p>
          </div>
        </div>

        <Tabs defaultValue="items" className="w-full">
          <TabsList className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700">
            <TabsTrigger value="items" data-testid="items-tab" className="data-[state=active]:bg-orange-500">Menü Öğeleri</TabsTrigger>
            <TabsTrigger value="categories" data-testid="categories-tab" className="data-[state=active]:bg-orange-500">Kategoriler</TabsTrigger>
          </TabsList>

          <TabsContent value="items" className="space-y-4">
            <Dialog open={showItemDialog} onOpenChange={(open) => {
              setShowItemDialog(open);
              if (!open) {
                setEditingItem(null);
                setNewItem({ name: '', description: '', price: '', category_id: '', image_url: '', available: true });
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-menu-item-button"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full"
                >
                  <Plus size={20} className="mr-2" />
                  Menü Öğesi Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-slate-900 dark:text-slate-100">
                    {editingItem ? 'Menü Öğesini Düzenle' : 'Yeni Menü Öğesi'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input
                    data-testid="item-name-input"
                    placeholder="Ürün Adı"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                  />
                  <Textarea
                    data-testid="item-description-input"
                    placeholder="Açıklama"
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                  />
                  <Input
                    data-testid="item-price-input"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Fiyat"
                    value={newItem.price}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || parseFloat(val) >= 0) {
                        setNewItem({ ...newItem, price: val });
                      }
                    }}
                    className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                  />
                  <Select value={newItem.category_id} onValueChange={(value) => setNewItem({ ...newItem, category_id: value })}>
                    <SelectTrigger data-testid="item-category-select" className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200">
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id} className="text-slate-800 dark:text-slate-200">
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="space-y-2">
                    <label className="text-sm text-slate-600 dark:text-slate-400">Ürün Görseli</label>
                    <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-4 hover:bg-slate-100 dark:bg-slate-800/50 transition-colors relative group">
                      {newItem.image_url ? (
                        <>
                          <img src={newItem.image_url} alt="Önizleme" className="h-32 w-auto object-cover rounded-md" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-xl">
                            <label htmlFor="menu-image-upload" className="cursor-pointer text-slate-900 dark:text-white flex flex-col items-center">
                              <span className="text-sm font-medium">Fotoğrafı Değiştir</span>
                            </label>
                          </div>
                        </>
                      ) : (
                        <label htmlFor="menu-image-upload" className="cursor-pointer flex flex-col items-center text-slate-600 dark:text-slate-400 hover:text-orange-400 transition-colors w-full py-4">
                          <span className="text-sm font-medium">Fotoğraf Seç veya Sürükle</span>
                        </label>
                      )}
                      <input
                        id="menu-image-upload"
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </div>
                  </div>

                  <Button
                    data-testid="save-menu-item"
                    onClick={handleAddOrUpdateItem}
                    className="w-full bg-orange-500 hover:bg-orange-600 mt-4"
                  >
                    {editingItem ? 'Güncelle' : 'Ekle'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <Card
                  key={item.id}
                  data-testid={`menu-item-${item.id}`}
                  className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 overflow-hidden hover:border-orange-500/30 transition-colors"
                >
                  {item.image_url && (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{item.name}</h3>
                      <span className="text-orange-400 font-mono font-bold text-lg">{item.price} ₺</span>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">{item.description}</p>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-slate-500">{getCategoryName(item.category_id)}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.available ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {item.available ? 'Mevcut' : 'Tükendi'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {isInventoryEnabled && (
                        <Button
                          onClick={() => handleOpenRecipeDialog(item)}
                          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold mb-1"
                          size="sm"
                        >
                          <Package size={16} className="mr-2" />
                          {item.recipe && item.recipe.length > 0 ? `${item.recipe.length} Hammadde Bağlı` : 'Reçete Tanımla'}
                        </Button>
                      )}
                      <Button
                        data-testid={`edit-item-${item.id}`}
                        onClick={() => handleEditItem(item)}
                        className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:bg-slate-700"
                        size="sm"
                      >
                        <Pencil size={16} className="mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        data-testid={`delete-item-${item.id}`}
                        onClick={() => handleDeleteItem(item.id)}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {items.length === 0 && (
              <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-12 text-center">
                <p className="text-slate-600 dark:text-slate-400">Henüz menü öğesi yok</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="categories" className="space-y-4">
            <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-category-button"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full"
                >
                  <FolderPlus size={20} className="mr-2" />
                  Kategori Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                <DialogHeader>
                  <DialogTitle className="text-slate-900 dark:text-slate-100">Yeni Kategori</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input
                    data-testid="category-name-input"
                    placeholder="Kategori Adı"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                  />
                  <Input
                    data-testid="category-order-input"
                    type="number"
                    placeholder="Sıralama"
                    value={newCategory.order}
                    onChange={(e) => setNewCategory({ ...newCategory, order: parseInt(e.target.value) || 0 })}
                    className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                  />
                  <Button
                    data-testid="save-category"
                    onClick={handleAddCategory}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    Ekle
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <Card
                  key={category.id}
                  data-testid={`category-${category.id}`}
                  className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-4 hover:border-orange-500/30 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{category.name}</h3>
                      <p className="text-slate-500 text-sm">Sıra: {category.order}</p>
                    </div>
                    <Button
                      data-testid={`delete-category-${category.id}`}
                      onClick={() => handleDeleteCategory(category.id)}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            {categories.length === 0 && (
              <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-12 text-center">
                <p className="text-slate-600 dark:text-slate-400">Henüz kategori yok</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Reçete Tanımlama Modalı (v1.0.5) */}
        <Dialog open={showRecipeDialog} onOpenChange={setShowRecipeDialog}>
          <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Package className="text-orange-500" size={22} />
                {selectedRecipeItem?.name} - Hammadde Reçetesi (BOM)
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded-xl text-sm text-slate-600 dark:text-slate-300">
                Bu üründen 1 porsiyon satıldığında depodan otomatik düşülecek hammaddeleri ve miktarlarını belirleyin.
              </div>

              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-2">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Hammadde Seçin</label>
                  <select
                    value={selectedInvId}
                    onChange={(e) => setSelectedInvId(e.target.value)}
                    className="w-full h-10 px-3 rounded-md bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none"
                  >
                    {inventoryItems.map(inv => (
                      <option key={inv.id} value={inv.id}>{inv.name} ({inv.unit})</option>
                    ))}
                  </select>
                </div>

                <div className="w-32 space-y-2">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Miktar</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Örn: 0.12"
                    value={recipeQty}
                    onChange={(e) => setRecipeQty(e.target.value)}
                    className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white font-mono"
                  />
                </div>

                <Button onClick={handleAddRecipeItem} className="bg-orange-600 hover:bg-orange-700 text-white font-bold h-10 px-4">
                  <Plus size={18} />
                </Button>
              </div>

              {/* List Recipe Items */}
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Reçete Kalemleri</h4>
                {recipeList.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Henüz hammadde bağlanmadı.</p>
                ) : (
                  recipeList.map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-100 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                      <div className="flex items-center gap-2">
                        <Package size={16} className="text-orange-400" />
                        <span className="text-sm font-bold text-slate-900 dark:text-white">{r.inventory_item_name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-sm font-bold text-orange-400">{r.quantity} {r.unit}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveRecipeItem(idx)} className="text-slate-500 hover:text-red-500 p-0 h-auto">
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-800">
                <Button variant="ghost" onClick={() => setShowRecipeDialog(false)}>İptal</Button>
                <Button onClick={handleSaveRecipe} className="bg-green-600 hover:bg-green-700 text-white font-bold px-6">
                  <Check size={18} className="mr-2" /> Reçeteyi Kaydet
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Silme Onay Modalı */}
        <Dialog open={deleteConfirm.isOpen} onOpenChange={(open) => !open && setDeleteConfirm({ isOpen: false, type: '', id: null })}>
          <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-white">Silme Onayı</DialogTitle>
            </DialogHeader>
            <div className="py-4 text-slate-700 dark:text-slate-300">
              Bu {deleteConfirm.type === 'category' ? 'kategoriyi' : 'menü öğesini'} silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirm({ isOpen: false, type: '', id: null })}>İptal</Button>
              <Button variant="destructive" onClick={confirmDelete}>Evet, Sil</Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </AdminLayout>
  );
};

export default MenuPage;